import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CurriculumApplyPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    applicant_name: '', qualification: '', specialty: '',
    institution_affiliation: '', curriculum_title: '', curriculum_summary: '', declaration: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.declaration) { toast.error('You must accept the declaration'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/applications/curriculum`, form, { headers: { Authorization: `Bearer ${token}` } });
      setSubmitted(true);
      toast.success('Application submitted successfully');
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else if (Array.isArray(errorDetail)) {
        // Handle FastAPI validation errors
        const errorMsg = errorDetail.map(e => e.msg || e.message).join(', ');
        toast.error(errorMsg || 'Validation failed');
      } else {
        toast.error('Submission failed');
      }
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#F8FAFC] py-16" data-testid="curriculum-apply-success">
        <Card className="imcac-card max-w-lg w-full mx-4">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading mb-2">Application Submitted</h2>
            <p className="text-sm text-slate-600 mb-6">Your curriculum standards application has been received and will be reviewed by the Standards Authority.</p>
            <Button onClick={() => window.location.href = '/dashboard'} className="bg-[#1B2A4A] text-white rounded-sm">Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="curriculum-apply-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Curriculum & Standards Authority Division</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Curriculum Application</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
        </div>
      </section>

      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container max-w-3xl">
          <Card className="imcac-card">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label className="text-sm">Applicant Name</Label><Input value={form.applicant_name} onChange={e => setForm({...form, applicant_name: e.target.value})} required className="rounded-sm mt-1" data-testid="curr-name" /></div>
                  <div><Label className="text-sm">Qualification</Label><Input value={form.qualification} onChange={e => setForm({...form, qualification: e.target.value})} required className="rounded-sm mt-1" data-testid="curr-qualification" /></div>
                  <div><Label className="text-sm">Specialty</Label><Input value={form.specialty} onChange={e => setForm({...form, specialty: e.target.value})} required className="rounded-sm mt-1" data-testid="curr-specialty" /></div>
                  <div><Label className="text-sm">Institution Affiliation</Label><Input value={form.institution_affiliation} onChange={e => setForm({...form, institution_affiliation: e.target.value})} required className="rounded-sm mt-1" data-testid="curr-institution" /></div>
                </div>
                <div><Label className="text-sm">Curriculum Title</Label><Input value={form.curriculum_title} onChange={e => setForm({...form, curriculum_title: e.target.value})} required className="rounded-sm mt-1" data-testid="curr-title" /></div>
                <div><Label className="text-sm">Curriculum Summary</Label><Textarea value={form.curriculum_summary} onChange={e => setForm({...form, curriculum_summary: e.target.value})} required rows={4} className="rounded-sm mt-1 resize-none" data-testid="curr-summary" /></div>
                <div className="p-4 bg-[#F8FAFC] border border-slate-200 rounded-sm">
                  <p className="text-sm text-slate-600 mb-2">Supporting Documents (upload during review stage)</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500"><Upload className="h-3.5 w-3.5" /> Document upload will be requested upon preliminary approval</div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-sm">
                  <Checkbox checked={form.declaration} onCheckedChange={v => setForm({...form, declaration: v})} data-testid="curr-declaration" />
                  <label className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                    I declare that all information provided is accurate and complete. I understand that IMCAC curriculum review is conducted against published competency standards.
                  </label>
                </div>
                <Button type="submit" disabled={loading || !form.declaration} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm" data-testid="curr-submit">
                  {loading ? 'Submitting...' : 'Submit Application'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
